import React, { useState, useContext } from 'react';
import {
    Card,
    Button,
    Space,
    Typography,
    Input,
    Row,
    Col,
    Table,
    Select,
    Modal,
    Upload,
    Tag,
    Divider,
    Alert,
    DatePicker
} from 'antd';
import {
    UploadOutlined,
    SearchOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
    StopOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { internalApi } from '../../common/axios';
import { USER_STATE, userStateList} from '../../shared/utils/enum';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { DarkModeContext } from '../../shared/contexts/darkModeContext';
import {buildUrl} from "../../shared/utils/net";

const { Text, Title } = Typography;
const { TextArea } = Input;

const WITHDRAW_REASON = {
    NONE: 0,
    SELF: 1,
    ADMIN: 2,
}
const GroupBan = () => {
    const { darkMode } = useContext(DarkModeContext);

    // 입력된 PID 문자열
    const [pidInput, setPidInput] = useState('');
    // 조회된 플레이어 리스트
    const [playerList, setPlayerList] = useState([]);
    // 선택된 플레이어 키 목록
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    // 적용할 상태
    const [targetState, setTargetState] = useState(USER_STATE.SUSPEND);
    // 정지 해제 날짜 (기본: 7일 후)
    const [stateDate, setStateDate] = useState(dayjs().add(7, 'day').endOf('day'));
    // 로딩 상태
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    // 확인 모달
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    // PID 파싱 (쉼표, 줄바꿈, 공백 구분)
    const parsePids = (input) => {
        return input
            .split(/[,\n\s]+/)
            .map(pid => pid.trim())
            .filter(pid => pid.length > 0);
    };

    // CSV 파일 업로드 처리
    const handleCSVUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const lines = content.split('\n');
            const pids = [];

            lines.forEach((line, index) => {
                // \r 제거 및 큰따옴표 제거
                const cleanedLine = line.replace(/\r/g, '').replace(/"/g, '').trim().toLowerCase();
                // 첫 번째 줄이 헤더(pid)인 경우 제외
                if (index === 0 && cleanedLine.includes('pid')) {
                    return;
                }

                // 각 줄에서 첫 번째 값(PID)만 추출 (\r, 큰따옴표 제거)
                const value = line.split(',')[0].replace(/\r/g, '').replace(/"/g, '').trim();
                // 빈 값이 아니고 영숫자로만 구성된 경우 유효한 PID
                if (value && /^[A-Za-z0-9]+$/.test(value)) {
                    pids.push(value);
                }
            });

            if (pids.length > 0) {
                const currentPids = pidInput ? pidInput + ',' : '';
                setPidInput(currentPids + pids.join(','));
                toast.success(`${pids.length}개의 PID를 불러왔습니다.`);
            } else {
                toast.error('유효한 PID를 찾을 수 없습니다.');
            }
        };
        reader.readAsText(file);
        return false; // 자동 업로드 방지
    };

    // 플레이어 조회
    const handleSearch = async () => {
        const pids = parsePids(pidInput);

        if (pids.length === 0) {
            toast.error('조회할 PID를 입력해주세요.');
            return;
        }

        if (pids.length > 1000) {
            toast.error('한 번에 최대 1000개까지 조회 가능합니다.');
            return;
        }

        setLoading(true);
        try {
            const { data } = await internalApi.get(buildUrl('player/groupBan', {pids}));
            setPlayerList(data.rows || []);
            setSelectedRowKeys(data.rows?.map(row => row.userKey) || []);
            toast.success(`${data.rows?.length || 0}명의 플레이어를 조회했습니다.`);
        } catch (err) {
            toast.error(err.response?.data?.message || '조회에 실패했습니다.');
            setPlayerList([]);
        } finally {
            setLoading(false);
        }
    };

    // 상태 변경 실행
    const handleExecute = async () => {
        if (selectedRowKeys.length === 0) {
            toast.error('처리할 플레이어를 선택해주세요.');
            return;
        }

        setActionLoading(true);
        try {
            const stateDateUnix = targetState === USER_STATE.SUSPEND
                ? stateDate.unix()
                : 0;

            await internalApi.post('/player/groupBan', {
                userKeys: selectedRowKeys,
                stateReason: WITHDRAW_REASON.ADMIN,
                userState: targetState,
                stateDate: stateDateUnix
            });

            toast.success(`${selectedRowKeys.length}명의 상태가 변경되었습니다.`);
            setConfirmModalOpen(false);
            // 리스트 새로고침
            handleSearch();
        } catch (err) {
            toast.error(err.response?.data?.message || '상태 변경에 실패했습니다.');
        } finally {
            setActionLoading(false);
        }
    };

    // 상태 텍스트 변환
    const getStateText = (state) => {
        switch (state) {
            case USER_STATE.WITHDRAW: return '탈퇴';
            case USER_STATE.SUSPEND: return '정지';
            default: return '정상';
        }
    };

    // 상태 태그 색상
    const getStateTagColor = (state) => {
        switch (state) {
            case USER_STATE.WITHDRAW: return 'red';
            case USER_STATE.SUSPEND: return 'orange';
            default: return 'green';
        }
    };

    // PID 검색 필터
    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`${dataIndex} 검색`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button
                        onClick={() => clearFilters && clearFilters()}
                        size="small"
                        style={{ width: 90 }}
                    >
                        초기화
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    });

    // 테이블 컬럼
    const columns = [
        {
            title: 'PID',
            dataIndex: 'pid',
            key: 'pid',
            width: 150,
            ...getColumnSearchProps('pid'),
        },
        {
            title: '닉네임',
            dataIndex: 'name',
            key: 'name',
            width: 150,
        },
        {
            title: '레벨',
            dataIndex: 'lv',
            key: 'lv',
            width: 80,
            align: 'center',
        },
        {
            title: '가입일',
            dataIndex: 'created',
            key: 'created',
            width: 120,
            render: (date) => date ? dayjs.unix(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '최근 접속',
            dataIndex: 'lastLogin',
            key: 'lastLogin',
            width: 120,
            render: (date) => date ? dayjs.unix(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '현재 상태',
            dataIndex: 'userState',
            key: 'userState',
            width: 100,
            align: 'center',
            render: (state) => (
                <Tag color={getStateTagColor(state)}>{getStateText(state)}</Tag>
            ),
        },
    ];

    // 테이블 행 선택
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    const cardTitle = (
        <Space align="center" size="middle">
            <div style={{
                background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <StopOutlined style={{ fontSize: '20px', color: 'white' }} />
            </div>
            <div>
                <Title level={4} style={{ margin: 0 }}>
                    Group Account Management
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    다중 계정 상태 관리
                </Text>
            </div>
        </Space>
    );

    return (
        <div style={{ padding: '24px' }}>
            <Card
                title={cardTitle}
                className={darkMode ? 'dark-theme' : 'light-theme'}
                styles={{
                    header: {
                        borderBottom: '1px solid var(--border-color)',
                        background: 'var(--header-background)',
                    },
                    body: {
                        padding: '24px',
                    }
                }}
            >
                {/* PID 입력 섹션 */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileTextOutlined style={{ color: '#1677ff' }} />
                        <Text strong>PID 입력</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            (쉼표, 줄바꿈, 공백으로 구분, 최대 1000개)
                        </Text>
                    </div>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={18}>
                            <TextArea
                                value={pidInput}
                                onChange={(e) => setPidInput(e.target.value)}
                                placeholder="PID를 입력하세요. 예: 123456, 789012, 345678"
                                rows={4}
                                style={{ fontFamily: 'monospace' }}
                            />
                        </Col>
                        <Col xs={24} lg={6}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <Upload
                                    accept=".csv,.txt"
                                    showUploadList={false}
                                    beforeUpload={handleCSVUpload}
                                >
                                    <Button icon={<UploadOutlined />} block>
                                        CSV 업로드
                                    </Button>
                                </Upload>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleSearch}
                                    loading={loading}
                                    block
                                >
                                    조회
                                </Button>
                                <Button
                                    onClick={() => {
                                        setPidInput('');
                                        setPlayerList([]);
                                        setSelectedRowKeys([]);
                                    }}
                                    block
                                >
                                    초기화
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <div style={{ marginTop: '8px' }}>
                        <Text type="secondary">
                            입력된 PID: <Text strong>{parsePids(pidInput).length}</Text>개
                        </Text>
                    </div>
                </div>

                <Divider />

                {/* 조회 결과 테이블 */}
                {playerList.length > 0 && (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            <Alert
                                message={
                                    <span>
                                        조회 결과: <strong>{playerList.length}</strong>명 /
                                        선택됨: <strong>{selectedRowKeys.length}</strong>명
                                    </span>
                                }
                                type="info"
                                showIcon
                            />
                        </div>

                        <Table
                            rowKey="userKey"
                            columns={columns}
                            dataSource={playerList}
                            rowSelection={rowSelection}
                            pagination={{
                                defaultPageSize: 50,
                                pageSizeOptions: ['20', '50', '100', '500'],
                                position: ['bottomCenter'],
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                showQuickJumper: true,
                            }}
                            scroll={{ x: 800 }}
                            size="small"
                            style={{ marginBottom: '24px' }}
                        />

                        <Divider />

                        {/* 상태 변경 섹션 */}
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <WarningOutlined style={{ color: '#faad14' }} />
                            <Text strong>상태 변경</Text>
                        </div>

                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={8} md={6}>
                                <Text>변경할 상태:</Text>
                            </Col>
                            <Col xs={24} sm={16} md={6}>
                                <Select
                                    value={targetState}
                                    onChange={setTargetState}
                                    style={{ width: '100%' }}
                                    options={userStateList.map(item => ({
                                        value: item.value,
                                        label: item.label,
                                    }))}
                                />
                            </Col>
                            {targetState === USER_STATE.SUSPEND && (
                                <>
                                    <Col xs={24} sm={8} md={4}>
                                        <Text>정지 해제일:</Text>
                                    </Col>
                                    <Col xs={24} sm={16} md={4}>
                                        <DatePicker
                                            value={stateDate}
                                            onChange={(value) => setStateDate(dayjs(value).endOf('day'))}
                                            format="YYYY-MM-DD"
                                            allowClear={false}
                                            style={{ width: '100%' }}
                                        />
                                    </Col>
                                </>
                            )}
                            {targetState === USER_STATE.WITHDRAW && (
                                <Col xs={24} md={8}>
                                    <Alert
                                        message="탈퇴 처리는 되돌릴 수 없습니다"
                                        type="error"
                                        showIcon
                                        size="small"
                                    />
                                </Col>
                            )}
                            <Col xs={24} md={4}>
                                <Button
                                    type="primary"
                                    danger={targetState === USER_STATE.WITHDRAW}
                                    icon={<ExclamationCircleOutlined />}
                                    onClick={() => setConfirmModalOpen(true)}
                                    disabled={selectedRowKeys.length === 0}
                                    block
                                >
                                    적용
                                </Button>
                            </Col>
                        </Row>
                    </>
                )}

                {/* 확인 모달 */}
                <Modal
                    title={
                        <Space>
                            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                            <span style={{ color: '#ff4d4f' }}>(!주의!)</span>
                            다중 계정 상태 변경 확인
                        </Space>
                    }
                    open={confirmModalOpen}
                    onCancel={() => setConfirmModalOpen(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setConfirmModalOpen(false)}>
                            취소
                        </Button>,
                        <Button
                            key="confirm"
                            type="primary"
                            danger={targetState === USER_STATE.WITHDRAW}
                            loading={actionLoading}
                            onClick={handleExecute}
                        >
                            확인
                        </Button>
                    ]}
                >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Text>
                            정말로 <Text strong style={{ color: '#1677ff' }}>{selectedRowKeys.length}명</Text>의
                            계정을 <Tag color={getStateTagColor(targetState)}>{getStateText(targetState)}</Tag>
                            상태로 변경하시겠습니까?
                        </Text>

                        {targetState === USER_STATE.SUSPEND && (
                            <Text>
                                정지 해제일: <Text strong>{stateDate.format('YYYY-MM-DD')}</Text>
                            </Text>
                        )}

                        {targetState === USER_STATE.WITHDRAW && (
                            <Text type="danger" strong>
                                <ExclamationCircleOutlined /> 이 작업은 되돌릴 수 없습니다.
                            </Text>
                        )}
                    </Space>
                </Modal>
            </Card>
        </div>
    );
};

export default GroupBan;